import { useDarkMode } from '../DarkModeContext';
import useIsVisible from '../isVisible';
import { useRef } from 'react';

const Chip = ({ text, dark }) => (
  <span className={`inline-block px-3 py-1 text-xs rounded-full border mr-2 mb-2 font-mono ${
    dark
      ? 'bg-Template-1/20 border-Template-2/40 text-Template-2'
      : 'bg-Template-0/10 border-Template-1/40 text-Template-0'
  }`}>{text}</span>
);

const Spec = ({ label, value, isDarkMode }) => (
  <div className={`inline-flex flex-col items-center rounded-xl px-5 py-3 mr-3 mb-3 border ${
    isDarkMode
      ? 'bg-gray-700/50 border-gray-600 text-white'
      : 'bg-Template-3/60 border-Template-2 text-Template-0'
  }`}>
    <span className="text-xs uppercase tracking-wider opacity-60 mb-1">{label}</span>
    <span className="text-lg font-bold">{value}</span>
  </div>
);

function Hardware() {
  const { isDarkMode } = useDarkMode();

  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const vis = [
    useIsVisible(refs[0]),
    useIsVisible(refs[1]),
    useIsVisible(refs[2]),
    useIsVisible(refs[3]),
    useIsVisible(refs[4]),
    useIsVisible(refs[5]),
  ];

  const sec = (i) => `px-8 py-12 transition-opacity ease-in duration-700 ${vis[i] ? 'opacity-100' : 'opacity-0'} ${
    isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
  }`;

  const h2 = `text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-Template-0'}`;
  const hr = `border-t-2 my-4 ${isDarkMode ? 'border-gray-600' : 'border-Template-2'}`;
  const body = `text-base leading-relaxed mb-4 max-w-3xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const li = `leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <>
      <div ref={refs[0]} className={sec(0)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Überblick</h2>
          <hr className={hr} />
          <p className={body}>
            Das Herzstück des SmartDice ist der <strong>ESP32-C3 Mini-1</strong> von Espressif – ein kompakter
            Mikrocontroller mit integrierter BLE-Antenne. Die Elektronik ist auf einer <strong>Flex-PCB</strong>{' '}
            untergebracht, die sich um die Seitenflächen des D20-Würfels faltet. Alle Bauteile werden vollständig
            bestückt bei <strong>JLCPCB</strong> gefertigt.
          </p>
          <div className="flex flex-wrap">
            <Chip text="ESP32-C3-MINI-1" dark={isDarkMode} />
            <Chip text="JLCPCB Assembly" dark={isDarkMode} />
            <Chip text="Flex-PCB" dark={isDarkMode} />
            <Chip text="BLE 5.0" dark={isDarkMode} />
          </div>
        </div>
      </div>

      <div ref={refs[1]} className={`${sec(1)} ${isDarkMode ? '' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>LEDs</h2>
          <hr className={hr} />
          <p className={body}>
            Jede aktive Würfelseite wird durch <strong>adressierbare 1×1 mm RGB-LEDs</strong> beleuchtet.
            Die LEDs sind in einem 7-Segment-Layout angeordnet und zeigen die gewürfelte Zahl direkt auf
            der entsprechenden Fläche an. Farbe und Helligkeit sind per App frei konfigurierbar.
          </p>
          <ul className={`list-disc pl-6 space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className={li}>1×1 mm adressierbare RGB-LEDs</li>
            <li className={li}>20× Dual-7-Segment-Anzeige pro Würfelseite</li>
            <li className={li}>Einzeln ansteuerbar über ESP32-C3 GPIO</li>
            <li className={li}>Farbe und Intensität via BLE konfigurierbar</li>
          </ul>
        </div>
      </div>

      <div ref={refs[2]} className={sec(2)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Spannungsversorgung</h2>
          <hr className={hr} />
          <p className={body}>
            Der Würfel wird von einem <strong>160 mAh LiPo-Akku</strong> betrieben. Das Laden erfolgt
            kabellos über den <strong>QI-Empfänger</strong> von Texas Instruments. Der{' '}
            <strong>MCP73871</strong> übernimmt das Li-Ion-Lademanagement und schützt vor Tiefentladung.
          </p>
          <div className="flex flex-wrap mb-4">
            <Spec label="Ladezeit" value="2 h" isDarkMode={isDarkMode} />
            <Spec label="Betriebszeit" value="4 h" isDarkMode={isDarkMode} />
            <Spec label="Kapazität" value="160 mAh" isDarkMode={isDarkMode} />
          </div>
          <ul className={`list-disc pl-6 space-y-2 text-sm`}>
            <li className={li}>LiPo 3,7 V / 160 mAh</li>
            <li className={li}>QI-Empfänger: bq51013B (Texas Instruments)</li>
            <li className={li}>Lademanagement: MCP73871 (Microchip)</li>
            <li className={li}>Schutz vor Tiefentladung durch Entkopplung von Akku und VCC</li>
          </ul>
        </div>
      </div>

      <div ref={refs[3]} className={`${sec(3)} ${isDarkMode ? '' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Accelerometer / IMU</h2>
          <hr className={hr} />
          <p className={body}>
            Der <strong>LSM6DSV</strong> von ST Microelectronics ist ein 9-Achsen-IMU mit integrierter{' '}
            <strong>Sensor Fusion Low Power (SFLP)</strong>. Er liefert per FIFO einen Gravitationsvektor,
            aus dem via Dot-Product mit 20 vorkalibrierten Flächennormalen die aktive Seite des Ikosaeders
            bestimmt wird. Die Kalibrierung wird in NVS gespeichert.
          </p>
          <div className="flex flex-wrap mb-4">
            <Chip text="LSM6DSV" dark={isDarkMode} />
            <Chip text="I2C" dark={isDarkMode} />
            <Chip text="SFLP Sensor Fusion" dark={isDarkMode} />
          </div>
          <ul className={`list-disc pl-6 space-y-2 text-sm`}>
            <li className={li}>9-Achsen IMU: Beschleunigung + Gyroskop + Magnetometer</li>
            <li className={li}>SFLP liefert Gravitationsvektor direkt aus der Hardware</li>
            <li className={li}><strong>Lookup Table</strong> mit 20 vordefinierten Flächennormalen des Ikosaeders</li>
            <li className={li}>Dot-Product des Gravitationsvektors mit allen 20 Normalen – höchster Wert gewinnt</li>
            <li className={li}>INT1: Wake-up (Würfel bewegt sich) / INT2: Settle (Würfel liegt still)</li>
          </ul>
        </div>
      </div>

      <div ref={refs[4]} className={sec(4)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Hall-Sensor</h2>
          <hr className={hr} />
          <p className={body}>
            Ein <strong>digitaler Hall-Sensor (Latch)</strong> erkennt das Magnetfeld der Ladestation,
            sobald der Würfel eingelegt wird. Der
            ESP32 geht in den <strong>Deep Sleep</strong>. Dadurch wird
            Energie im Ruhezustand gespart.
          </p>
          <ul className={`list-disc pl-6 space-y-2 text-sm`}>
            <li className={li}>Digitaler Hall-Latch</li>
            <li className={li}>Interrupt-fähig: löst direkt am ESP32-C3 GPIO aus</li>
            <li className={li}>Trigger für State-Wechsel zu ChargingStation-Zustand</li>
            <li className={li}>Im Deep-Sleep-Zustand übernimmt der QI-Sender die Stromversorgung</li>
          </ul>
        </div>
      </div>

      <div ref={refs[5]} className={`${sec(5)} ${isDarkMode ? '' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Kabelloses Laden (QI)</h2>
          <hr className={hr} />
          <p className={body}>
            Der Würfel wird nach dem <strong>QI-Standard</strong> kabellos geladen. Als Sender dient ein
            handelsübliches <strong>QI-Lademodul mit USB-C</strong> – eine eigene smarte Ladestation gibt es
            nicht. Der Würfel muss lediglich auf der Sendespule aufliegen, dann startet das Laden automatisch.
            Auf der Würfelseite empfängt der <strong>QI-Empfänger (bq51013B)</strong> die Energie und speist
            das Lademanagement.
          </p>
          <div className="flex flex-wrap mb-4">
            <Chip text="QI Standard" dark={isDarkMode} />
            <Chip text="USB-C Sender" dark={isDarkMode} />
            <Chip text="bq51013B Empfänger" dark={isDarkMode} />
          </div>
          <ul className={`list-disc pl-6 space-y-2 text-sm`}>
            <li className={li}>Kompatibel mit handelsüblichen QI-Ladepads – kein eigener Microcontroller nötig</li>
            <li className={li}>USB-C als Energiequelle des QI-Senders</li>
            <li className={li}>Würfel auflegen → lädt. Würfel abnehmen → fertig</li>
            <li className={li}>Keine smarte Logik auf Sendeseite, das Lademanagement sitzt im Würfel</li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default Hardware;
